/**
 * Widget tiering — which widgets each plan unlocks.
 *
 * The editor uses this to render lock badges in the palette and to filter
 * the system prompt sent to the AI. The adapter uses it to validate AI
 * output (refuses widgets the active plan doesn't include).
 *
 * Adding a new widget? Default it to "free" and bump it up only after
 * a product call — silently treating new widgets as Pro is unfair to
 * existing free-tier users.
 */
/**
 * Source of truth for which widgets each plan can use. Order in source
 * is alphabetical — keep it that way for readability of the diff when
 * re-tiering.
 */
export const WIDGET_TIERS = {
    // --- Free (10) — bedrock content blocks every site needs.
    heading: "free",
    text: "free",
    image: "free",
    button: "free",
    divider: "free",
    spacer: "free",
    hero: "free",
    html: "free",
    video: "free",
    "icon-list": "free",
    // --- Pro (25) — marketing/conversion blocks.
    accordion: "pro",
    alert: "pro",
    audio: "pro",
    badge: "pro",
    breadcrumbs: "pro",
    carousel: "pro",
    countdown: "pro",
    "cta-banner": "pro",
    faq: "pro",
    features: "pro",
    gallery: "pro",
    "logo-bar": "pro",
    "nav-menu": "pro",
    newsletter: "pro",
    pricing: "pro",
    progress: "pro",
    quote: "pro",
    share: "pro",
    "social-links": "pro",
    stats: "pro",
    steps: "pro",
    tabs: "pro",
    team: "pro",
    testimonials: "pro",
    timeline: "pro",
    // --- Agency (5) — high-value building blocks for client work.
    chart: "agency",
    code: "agency",
    form: "agency",
    map: "agency",
    "product-card": "agency",
};
/**
 * Plan inclusion order. Pro includes free; Agency includes pro and free.
 */
const PLAN_INCLUDES = {
    free: ["free"],
    pro: ["free", "pro"],
    agency: ["free", "pro", "agency"],
};
/** Resolve PLAN_INCLUDES with a free-plan fallback for unknown / undefined plan values. */
function tiersFor(plan) {
    return PLAN_INCLUDES[plan] ?? PLAN_INCLUDES.free;
}
/**
 * Return the widget types available to a given plan, sorted alphabetically.
 * Unknown widget types in WIDGET_TIERS (e.g. user-registered customs not
 * yet tiered) default to free so they don't disappear silently. An unknown
 * `plan` falls back to "free" via {@link tiersFor} so a corrupted KV value
 * can't crash the palette.
 */
export function getAvailableWidgets(plan) {
    const allowed = new Set(tiersFor(plan));
    return Object.entries(WIDGET_TIERS)
        .filter(([, tier]) => allowed.has(tier))
        .map(([type]) => type)
        .sort();
}
/**
 * Whether a specific widget type is unlocked for the given plan. Widgets
 * not in WIDGET_TIERS are treated as free (see {@link getAvailableWidgets}).
 */
export function isWidgetAvailable(type, plan) {
    const tier = WIDGET_TIERS[type] ?? "free";
    return tiersFor(plan).includes(tier);
}
/**
 * Look up the tier of a widget type. Returns "free" for unknown types.
 */
export function getWidgetTier(type) {
    return WIDGET_TIERS[type] ?? "free";
}
//# sourceMappingURL=tiers.js.map